import { useState, useEffect } from "react";

import { DISPATCH_VEHICLES, HEAVY_VEHICLES, canServeRides, DECLARED_TO_DISPATCH } from "../lib/dispatchGrid";

export default function Verifications({ supabase }) {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [rejectionReason, setRejectionReason] = useState("");
  const [driverInfo, setDriverInfo] = useState(null);   // { declared, dispatch } for rider-only apps
  // B-3: the verification bucket is PRIVATE (structured, guessable
  // object names made public serving untenable). Stored URLs keep the
  // old public form; we derive the path and sign per view. Signing
  // requires the is_admin() SELECT policy on storage.objects.
  const [docUrls, setDocUrls] = useState({});
  useEffect(() => {
    (async () => {
      if (!selected) { setDocUrls({}); return; }
      const urls = [
        selected.id_document_url, selected.business_photo_url, selected.business_photo_2_url,
        driverInfo?.declared?.vehicle_photo_url, driverInfo?.declared?.license_photo_url,
        driverInfo?.declared?.insurance_photo_url,
      ].filter(u => u && u.includes("/verification/"));
      if (!urls.length) { setDocUrls({}); return; }
      const paths = urls.map(u => u.split("/verification/")[1]);
      const { data } = await supabase.storage.from("verification").createSignedUrls(paths, 3600);
      const map = {};
      urls.forEach((u, i) => { if (data?.[i]?.signedUrl) map[u] = data[i].signedUrl; });
      setDocUrls(map);
    })();
  }, [selected, driverInfo]); // eslint-disable-line react-hooks/exhaustive-deps
  const docSrc = (u) => docUrls[u] || u;
  const [vehicleType, setVehicleType] = useState("");   // admin's dispatch-type pick
  const [approvedDelivery, setApprovedDelivery] = useState(false); // capability grants (5b)
  const [approvedRides, setApprovedRides] = useState(false);
  const [capReqs, setCapReqs] = useState([]);           // capability requests (5c-3)
  const [capDrivers, setCapDrivers] = useState({});     // profile_id -> drivers row context
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => { loadApplications(); loadCapRequests(); }, []);

  async function loadApplications() {
    const { data } = await supabase
      .from("shops")
      .select("*, profiles(full_name, email, phone)")
      .order("submitted_at", { ascending: false });
    if (data) setShops(data);
    setLoading(false);
  }

  async function callNotifyFunction(shopId, type) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch("https://zifhuekyaqltvrqgstco.supabase.co/functions/v1/notify-provider-approval", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ shop_id: shopId, type }),
      });
    } catch (e) {
      console.error("Notification failed:", e);
    }
  }

  async function loadCapRequests() {
    // Two FKs point at profiles (driver_profile_id, reviewed_by) — the embed
    // MUST name its FK or PostgREST throws PGRST201 (the Orders-tab lesson).
    const { data } = await supabase
      .from("capability_requests")
      .select("*, profiles!capability_requests_driver_profile_id_fkey(full_name, phone, email)")
      .order("requested_at", { ascending: false });
    setCapReqs(data || []);
    const ids = [...new Set((data || []).map(r => r.driver_profile_id))];
    if (ids.length) {
      const { data: drvs } = await supabase.from("drivers")
        .select("profile_id, vehicle_type, approved_delivery, approved_rides, is_verified")
        .in("profile_id", ids);
      setCapDrivers(Object.fromEntries((drvs || []).map(d => [d.profile_id, d])));
    }
  }

  async function approveCapRequest(req) {
    const name = req.profiles?.full_name || "this driver";
    const drv = capDrivers[req.driver_profile_id];
    if (req.capability === "rides" && HEAVY_VEHICLES.includes(drv?.vehicle_type)) {
      // Hard block (5d grid): the server would nullify the grant anyway —
      // vehicle_can_serve never matches heavy vehicles to rides.
      alert(`${name} drives a ${(drv?.vehicle_type || "heavy vehicle").toUpperCase()} — heavy vehicles cannot take passengers. Rides cannot be approved for this driver.`);
      return;
    }
    if (!window.confirm(`Grant the ${req.capability} capability to ${name}?`)) return;

    // Exactly the REQUESTED capability, keyed off the request row.
    const col = req.capability === "delivery" ? "approved_delivery" : "approved_rides";
    const acceptsCol = req.capability === "delivery" ? "accepts_delivery" : "accepts_rides";

    // RULE (5c-3): grant FIRST, mark the request SECOND — a partial failure
    // must leave the driver WITH the granted capability (stale pending
    // badge at worst), never approved-on-paper-only.
    const { error: drvErr } = await supabase.from("drivers")
      .update({ [col]: true, [acceptsCol]: true })   // accepts aligned at grant (5b rule)
      .eq("profile_id", req.driver_profile_id);
    if (drvErr) { alert("Grant failed — nothing changed: " + drvErr.message); return; }

    const { data: { session } } = await supabase.auth.getSession();
    const { error: reqErr } = await supabase.from("capability_requests")
      .update({ status: "approved", reviewed_at: new Date().toISOString(), reviewed_by: session?.user?.id })
      .eq("id", req.id);
    if (reqErr) alert("Capability GRANTED (the driver has it) but the request row failed to update — it will still show as pending: " + reqErr.message);
    loadCapRequests();
  }

  async function denyCapRequest(req) {
    const reason = window.prompt(`Deny ${req.profiles?.full_name || "this driver"}'s ${req.capability} request — reason (shown to the driver):`);
    if (!reason || !reason.trim()) return;   // reason required; cancel/empty aborts
    const { data: { session } } = await supabase.auth.getSession();
    const { error } = await supabase.from("capability_requests")
      .update({ status: "denied", denial_reason: reason.trim(), reviewed_at: new Date().toISOString(), reviewed_by: session?.user?.id })
      .eq("id", req.id);
    if (error) { alert("Deny failed: " + error.message); return; }
    loadCapRequests();
  }

  function capBadge(r) {
    // withdrawn (reviewed_by NULL) is DRIVER-initiated — never render it
    // like an admin action.
    return r.status === "approved" ? { text: "Approved", color: "#00A651" }
      : r.status === "denied" ? { text: "Denied: " + (r.denial_reason || "no reason"), color: "#e74c3c" }
      : { text: "Withdrawn by driver", color: "#888" };
  }

  async function openReview(shop) {
    setSelected(shop);
    setDriverInfo(null);
    setVehicleType("");
    setApprovedDelivery(false);
    setApprovedRides(false);
    if (shop.is_rider_only && shop.owner_id) {
      const [{ data: declared }, { data: dispatch }] = await Promise.all([
        supabase.from("driver_profiles").select("*").eq("owner_id", shop.owner_id).maybeSingle(),
        supabase.from("drivers").select("*").eq("profile_id", shop.owner_id).maybeSingle(),
      ]);
      setDriverInfo({ declared, dispatch });
      const initialVehicle = dispatch?.vehicle_type || DECLARED_TO_DISPATCH[declared?.vehicle_type] || "";
      setVehicleType(initialVehicle);
      // Vehicle-derived defaults; the admin confirms at Approve (the confirm
      // dialog names the grants). Heavy vehicles (van/truck) default to none
      // (hauling runs on accepts_hauling; approval column deferred).
      setApprovedDelivery(canServeRides(initialVehicle));
      setApprovedRides(canServeRides(initialVehicle));
    }
  }

  async function approveShop(shop) {
    const isRider = !!shop.is_rider_only;
    if (isRider && !vehicleType) {
      alert("Select the driver's vehicle type before approving — without it the driver cannot be dispatched.");
      return;
    }
    const capsLabel = [approvedDelivery && "Delivery", approvedRides && "Rides"].filter(Boolean).join(" + ");
    if (isRider) {
      // Capabilities are approvals — name exactly what is being granted.
      const ok = window.confirm(capsLabel
        ? `Approve this driver for: ${capsLabel}?`
        : HEAVY_VEHICLES.includes(vehicleType)
        ? `Approve this ${vehicleType} driver as hauling-only? (Hauling is enabled via the vehicle type; delivery/rides capabilities don't apply.)`
        : "No capabilities selected — the driver will be verified but cannot receive ANY work until a capability is granted. Approve anyway?");
      if (!ok) return;
    }

    const { error: shopErr } = await supabase.from("shops").update({
      verification_status: "approved",
      is_active: true,
      last_activity_at: new Date().toISOString(),
      reviewed_at: new Date().toISOString(),
    }).eq("id", shop.id);
    if (shopErr) { alert("Approval failed: " + shopErr.message); return; }

    if (isRider) {
      // Sync the dispatch record: RLS only shows orders to drivers with
      // is_verified = true, and ride dispatch matches on vehicle_type.
      const flags = {
        is_verified: true,
        vehicle_type: vehicleType,
        accepts_delivery: true,
        // accepts must not contradict a granted approval (effective
        // capability = approved AND accepts).
        accepts_rides: approvedRides || vehicleType !== "truck",
        accepts_hauling: vehicleType === "truck",
        approved_delivery: approvedDelivery,
        approved_rides: approvedRides,
      };
      const { data: existing } = await supabase
        .from("drivers").select("id").eq("profile_id", shop.owner_id).maybeSingle();
      const { error: drvErr } = existing
        ? await supabase.from("drivers").update(flags).eq("profile_id", shop.owner_id)
        : await supabase.from("drivers").insert({ profile_id: shop.owner_id, is_active: false, ...flags });
      if (drvErr) {
        alert("Shop record approved but the driver record failed to update — the driver will NOT see orders yet: " + drvErr.message);
        return;
      }
      // Approval is the sole grantor of driver status (is_driver is no longer
      // self-set at registration).
      await supabase.from("profiles").update({ is_driver: true }).eq("id", shop.owner_id);
    } else if (shop.owner_id) {
      // Shop owners only — setting this for riders flips them out of the rider UI.
      await supabase.from("profiles").update({
        is_shop_owner: true,
      }).eq("id", shop.owner_id);
    }

    await callNotifyFunction(shop.id, "approved");
    setSelected(null);
    loadApplications();
    alert(isRider
      ? `Driver approved and verified for: ${capsLabel || "no capabilities"}. They can now go active in the app. Provider has been notified.`
      : "Shop approved! Provider has been notified by email and push notification.");
  }

  async function rejectShop(shop) {
    if (!rejectionReason.trim()) { alert("Please enter a rejection reason"); return; }

    await supabase.from("shops").update({
      verification_status: "rejected",
      is_active: false,
      rejection_reason: rejectionReason,
      reviewed_at: new Date().toISOString(),
    }).eq("id", shop.id);

    if (shop.is_rider_only && shop.owner_id) {
      // Revoke dispatch access AND capabilities; no-op if the drivers row
      // doesn't exist. A rejected driver must not retain capability grants.
      await supabase.from("drivers")
        .update({ is_verified: false, approved_delivery: false, approved_rides: false })
        .eq("profile_id", shop.owner_id);
      await supabase.from("profiles").update({ is_driver: false }).eq("id", shop.owner_id);
    }

    await callNotifyFunction(shop.id, "rejected");
    setSelected(null);
    setRejectionReason("");
    loadApplications();
    alert("Application rejected. Provider has been notified by email and push notification.");
  }

  const statusColor = { pending: "#f39c12", approved: "#00A651", rejected: "#e74c3c" };
  const filtered = shops.filter(s => filter === "all" || s.verification_status === filter);

  const pendingCap = capReqs.filter(r => r.status === "pending");
  const resolvedCap = capReqs.filter(r => r.status !== "pending");

  return (
    <div>
      <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: (pendingCap.length > 0 || showResolved) ? 12 : 0 }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            Capability Requests{pendingCap.length > 0 && <span style={{ color: "#f39c12" }}> ({pendingCap.length} pending)</span>}
          </div>
          <button style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 20, padding: "5px 12px", color: "#999", cursor: "pointer", fontSize: 12 }} onClick={() => setShowResolved(!showResolved)}>
            {showResolved ? "Hide resolved" : "Show resolved"}
          </button>
        </div>
        {pendingCap.length === 0 && !showResolved && (
          <div style={{ color: "#666", fontSize: 13, marginTop: 8 }}>No pending capability requests.</div>
        )}
        {pendingCap.map(r => {
          const d = capDrivers[r.driver_profile_id];
          return (
            <div key={r.id} style={{ background: "#1a1a1a", borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ color: "#fff", fontWeight: 600 }}>{r.profiles?.full_name || "Driver"}</span>
                <span style={{ color: "#999", fontSize: 12 }}>{r.profiles?.phone || r.profiles?.email || ""}</span>
                <span style={{ background: r.capability === "delivery" ? "#0057A830" : "#9b59b630", color: r.capability === "delivery" ? "#4da3ff" : "#c39bd3", borderRadius: 10, padding: "2px 10px", fontSize: 12, fontWeight: 700, textTransform: "capitalize" }}>{r.capability}</span>
                <span style={{ color: "#666", fontSize: 12 }}>{new Date(r.requested_at).toLocaleDateString()}</span>
              </div>
              {r.note && <div style={{ color: "#ccc", fontSize: 13, fontStyle: "italic", marginTop: 6 }}>"{r.note}"</div>}
              {d && (
                <div style={{ color: "#666", fontSize: 12, marginTop: 6 }}>
                  Vehicle: {d.vehicle_type || "—"} · currently approved: {[d.approved_delivery && "delivery", d.approved_rides && "rides"].filter(Boolean).join(", ") || "none"}{d.is_verified ? "" : " · NOT VERIFIED"}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button style={{ background: "#00A651", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }} onClick={() => approveCapRequest(r)}>Approve</button>
                <button style={{ background: "#e74c3c20", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }} onClick={() => denyCapRequest(r)}>Deny</button>
              </div>
            </div>
          );
        })}
        {showResolved && resolvedCap.length === 0 && (
          <div style={{ color: "#666", fontSize: 13 }}>No resolved requests yet.</div>
        )}
        {showResolved && resolvedCap.map(r => {
          const badge = capBadge(r);
          return (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: "1px solid #222", flexWrap: "wrap" }}>
              <span style={{ color: "#ccc", fontSize: 13 }}>{r.profiles?.full_name || "Driver"}</span>
              <span style={{ color: "#999", fontSize: 12, textTransform: "capitalize" }}>{r.capability}</span>
              <span style={{ color: badge.color, fontSize: 12, fontWeight: 600 }}>{badge.text}</span>
              <span style={{ color: "#555", fontSize: 12 }}>{r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString() : ""}</span>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {["all", "pending", "approved", "rejected"].map(f => (
            <button key={f} style={{ background: filter === f ? "#00A65120" : "#111", border: filter === f ? "1px solid #00A651" : "1px solid #333", borderRadius: 20, padding: "6px 14px", color: filter === f ? "#00A651" : "#666", cursor: "pointer", fontSize: 13, textTransform: "capitalize" }} onClick={() => setFilter(f)}>
              {f} {f !== "all" && `(${shops.filter(s => s.verification_status === f).length})`}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div style={{ color: "#666", textAlign: "center", padding: 40 }}>Loading applications...</div> : (
        <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 1fr" : "1fr", gap: 16 }}>
          <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>{["Business", "Owner", "Type", "Submitted", "Status", "Action"].map(h => <th key={h} style={{ color: "#666", fontSize: 12, fontWeight: "600", textAlign: "left", padding: "12px 16px", borderBottom: "1px solid #222", textTransform: "uppercase", background: "#0a0a0a" }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map(shop => (
                  <tr key={shop.id} style={{ borderBottom: "1px solid #1a1a1a", background: selected?.id === shop.id ? "#1a1a1a" : "transparent" }}>
                    <td style={{ color: "#fff", fontSize: 14, padding: "12px 16px", fontWeight: "600" }}>
                      {shop.name || "N/A"}
                      <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: "600", background: shop.is_rider_only ? "#9b59b620" : "#0057A820", color: shop.is_rider_only ? "#9b59b6" : "#0057A8" }}>
                        {shop.is_rider_only ? "🏍 Driver" : "🏪 Shop"}
                      </span>
                    </td>
                    <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>{shop.profiles?.full_name || "N/A"}</td>
                    <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>{shop.business_type || "N/A"}</td>
                    <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>{shop.submitted_at ? new Date(shop.submitted_at).toLocaleDateString() : "N/A"}</td>
                    <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "600", background: (statusColor[shop.verification_status] || "#999") + "20", color: statusColor[shop.verification_status] || "#999", textTransform: "capitalize" }}>{shop.verification_status || "not submitted"}</span>
                    </td>
                    <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>
                      <button style={{ background: "#0057A820", color: "#0057A8", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: "600", cursor: "pointer" }} onClick={() => openReview(shop)}>Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selected && (
            <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 24, overflowY: "auto", maxHeight: "80vh" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h3 style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Application Review</h3>
                <button style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 20 }} onClick={() => setSelected(null)}>×</button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Business Name</div>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>{selected.name}</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Owner</div>
                  <div style={{ color: "#fff", fontSize: 14 }}>{selected.profiles?.full_name}</div>
                </div>
                <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Email</div>
                  <div style={{ color: "#fff", fontSize: 14, wordBreak: "break-all" }}>{selected.profiles?.email || "N/A"}</div>
                </div>
                <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Phone</div>
                  <div style={{ color: "#fff", fontSize: 14 }}>{selected.profiles?.phone || "N/A"}</div>
                </div>
                <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Business Type</div>
                  <div style={{ color: "#fff", fontSize: 14 }}>{selected.business_type || "N/A"}</div>
                </div>
                <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Physical Location</div>
                  <div style={{ color: selected.is_brick_and_mortar ? "#00A651" : "#666", fontSize: 14 }}>{selected.is_brick_and_mortar ? "Yes" : "No"}</div>
                </div>
              </div>

              {selected.business_address && (
                <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Business Address</div>
                  <div style={{ color: "#fff", fontSize: 14 }}>{selected.business_address}</div>
                </div>
              )}

              {selected.description && (
                <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Description</div>
                  <div style={{ color: "#ccc", fontSize: 14, lineHeight: "1.6" }}>{selected.description}</div>
                </div>
              )}

              {selected.id_document_url && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>ID Document</div>
                  <img src={docSrc(selected.id_document_url)} alt="ID Document" style={{ width: "100%", borderRadius: 8, border: "1px solid #333" }} />
                </div>
              )}

              {selected.business_photo_url && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>Business Photo 1</div>
                  <img src={docSrc(selected.business_photo_url)} alt="Business" style={{ width: "100%", borderRadius: 8, border: "1px solid #333" }} />
                </div>
              )}

              {selected.business_photo_2_url && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>Business Photo 2</div>
                  <img src={docSrc(selected.business_photo_2_url)} alt="Business 2" style={{ width: "100%", borderRadius: 8, border: "1px solid #333" }} />
                </div>
              )}

              {selected.is_rider_only && (
                <div style={{ border: "1px solid #9b59b6", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ color: "#9b59b6", fontSize: 13, fontWeight: "700", marginBottom: 10 }}>🏍 Driver Application</div>

                  {driverInfo?.declared ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                      {[
                        { label: "Declared Vehicle", value: driverInfo.declared.vehicle_type || "—" },
                        { label: "Make / Model", value: [driverInfo.declared.vehicle_make, driverInfo.declared.vehicle_model, driverInfo.declared.vehicle_year].filter(Boolean).join(" ") || "—" },
                        { label: "Plate", value: driverInfo.declared.vehicle_plate || driverInfo.declared.license_plate || "—" },
                        { label: "License #", value: driverInfo.declared.license_number || "—" },
                        { label: "Driver Types", value: (driverInfo.declared.driver_types || []).join(", ").replace(/_/g, " ") || "—" },
                        { label: "Dispatch Record", value: driverInfo.dispatch ? `exists · ${driverInfo.dispatch.is_verified ? "verified" : "unverified"} · ${driverInfo.dispatch.vehicle_type || "no vehicle"}` : "none yet" },
                      ].map(f => (
                        <div key={f.label} style={{ background: "#1a1a1a", borderRadius: 8, padding: 10 }}>
                          <div style={{ color: "#666", fontSize: 10, textTransform: "uppercase", marginBottom: 3 }}>{f.label}</div>
                          <div style={{ color: "#fff", fontSize: 13 }}>{f.value}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
                      No vehicle details submitted yet (driver fills these in under Account → My Vehicles &amp; Registration).
                    </div>
                  )}

                  {driverInfo?.declared?.vehicle_photo_url && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 6 }}>Vehicle Photo</div>
                      <img src={docSrc(driverInfo.declared.vehicle_photo_url)} alt="Vehicle" style={{ width: "100%", borderRadius: 8, border: "1px solid #333" }} />
                    </div>
                  )}
                  {driverInfo?.declared?.license_photo_url && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 6 }}>Driver's License</div>
                      <img src={docSrc(driverInfo.declared.license_photo_url)} alt="License" style={{ width: "100%", borderRadius: 8, border: "1px solid #333" }} />
                    </div>
                  )}
                  {driverInfo?.declared?.insurance_photo_url && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 6 }}>Insurance</div>
                      <img src={docSrc(driverInfo.declared.insurance_photo_url)} alt="Insurance" style={{ width: "100%", borderRadius: 8, border: "1px solid #333" }} />
                    </div>
                  )}

                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>
                    Dispatch Vehicle Type <span style={{ color: "#e74c3c" }}>* required to approve</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                    {DISPATCH_VEHICLES.map(v => (
                      <button key={v}
                        style={{ background: vehicleType === v ? "#9b59b6" : "#1a1a1a", color: vehicleType === v ? "#fff" : "#999", border: vehicleType === v ? "1px solid #9b59b6" : "1px solid #333", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: "600", cursor: "pointer", textTransform: "capitalize" }}
                        onClick={() => { setVehicleType(v); setApprovedDelivery(canServeRides(v)); setApprovedRides(canServeRides(v)); }}>
                        {v}
                      </button>
                    ))}
                  </div>
                  <div style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>
                    Van/Truck ⇒ hauling + delivery (no passenger rides). Motorcycle/Tricycle/Car ⇒ rides + delivery.
                  </div>
                  <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>
                    Capabilities to Approve
                  </div>
                  {[
                    { label: "Delivery (incl. marketplace orders)", value: approvedDelivery, set: setApprovedDelivery, disabled: false },
                    { label: "Rides (passenger transport)", value: approvedRides, set: setApprovedRides, disabled: HEAVY_VEHICLES.includes(vehicleType) },
                  ].map(c => (
                    <label key={c.label} style={{ display: "flex", alignItems: "center", gap: 8, color: c.disabled ? "#555" : "#ccc", fontSize: 13, marginBottom: 6, cursor: c.disabled ? "not-allowed" : "pointer" }}>
                      <input type="checkbox" checked={c.value} disabled={c.disabled} onChange={e => c.set(e.target.checked)} />
                      {c.label}{c.disabled ? " — heavy vehicles can't take passengers" : ""}
                    </label>
                  ))}
                  {HEAVY_VEHICLES.includes(vehicleType) && (
                    <div style={{ color: "#f39c12", fontSize: 12, marginBottom: 6 }}>
                      Heavy vehicle: hauling is enabled via the vehicle type — leaving delivery/rides unchecked is expected, not an error.
                    </div>
                  )}
                  <div style={{ color: "#666", fontSize: 12 }}>
                    Defaults follow the vehicle; confirm before approving. Capabilities are approvals — the driver cannot self-grant more later.
                  </div>
                </div>
              )}

              {selected.verification_status === "pending" && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>Rejection Reason (if rejecting)</div>
                    <textarea
                      style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: 12, color: "#fff", fontSize: 14, minHeight: 80, resize: "vertical", fontFamily: "inherit" }}
                      placeholder="Enter reason for rejection..."
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e.target.value)}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button style={{ flex: 1, background: "#e74c3c20", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: "600", cursor: "pointer" }} onClick={() => rejectShop(selected)}>
                      ❌ Reject Application
                    </button>
                    <button style={{ flex: 1, background: selected.is_rider_only && !vehicleType ? "#333" : "#00A651", color: selected.is_rider_only && !vehicleType ? "#777" : "#fff", border: "none", borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: "600", cursor: "pointer" }} onClick={() => approveShop(selected)}>
                      ✅ Approve & Activate
                    </button>
                  </div>
                </div>
              )}

              {selected.verification_status === "approved" && (
                <div style={{ background: "#00A65120", border: "1px solid #00A651", borderRadius: 8, padding: 12, textAlign: "center", color: "#00A651", fontWeight: "600" }}>
                  ✅ This application has been approved
                </div>
              )}

              {selected.verification_status === "rejected" && (
                <div>
                  <div style={{ background: "#e74c3c20", border: "1px solid #e74c3c", borderRadius: 8, padding: 12, textAlign: "center", color: "#e74c3c", fontWeight: "600", marginBottom: 12 }}>
                    ❌ This application was rejected
                  </div>
                  {selected.rejection_reason && (
                    <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12 }}>
                      <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 4 }}>Rejection Reason</div>
                      <div style={{ color: "#ccc", fontSize: 14 }}>{selected.rejection_reason}</div>
                    </div>
                  )}
                  <button style={{ width: "100%", background: "#00A651", color: "#fff", border: "none", borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: "600", cursor: "pointer", marginTop: 12 }} onClick={() => approveShop(selected)}>
                    ✅ Override & Approve
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
